'use client';

import { useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import Link from '@/components/compat/Link';
import { getAdminSession, hydrateAdminSession, isAdminAuthenticated } from '@/lib/admin-auth';
import {
  sessionHasPermission,
  type PainelPermission,
} from '@/lib/painel-rbac';
import type { AdminSession } from '@/services/auth.service';
import { cn } from '@/lib/utils';

/** Sessão do painel nas páginas públicas (sem PainelSessionProvider). */
export function usePublicCmsSession(): {
  session: AdminSession | null;
  hydrated: boolean;
} {
  const [session, setSession] = useState<AdminSession | null>(() => getAdminSession());
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const sync = () => setSession(getAdminSession());
    window.addEventListener('xat-auth-changed', sync);

    void (async () => {
      if (!isAdminAuthenticated()) {
        setSession(null);
        setHydrated(true);
        return;
      }
      const remote = await hydrateAdminSession();
      setSession(remote ?? getAdminSession());
      setHydrated(true);
    })();

    return () => window.removeEventListener('xat-auth-changed', sync);
  }, []);

  return { session, hydrated };
}

type CmsPublicEditButtonProps = {
  href: string | null | undefined;
  permission: PainelPermission | PainelPermission[];
  label?: string;
  className?: string;
  /** `dark` = páginas powers/aces; `light` = guias. */
  tone?: 'dark' | 'light';
  /**
   * `floating` = canto (legado).
   * `inline` = fluxo claro (barra do guia).
   * `hero` = linha dos breadcrumbs no hero escuro.
   */
  variant?: 'floating' | 'inline' | 'hero';
};

/**
 * Botão «Editar» visível só para editores autenticados com a permissão certa.
 */
export function CmsPublicEditButton({
  href,
  permission,
  label = 'Editar',
  className,
  tone = 'dark',
  variant = 'floating',
}: CmsPublicEditButtonProps) {
  const { session, hydrated } = usePublicCmsSession();

  const permissions = Array.isArray(permission) ? permission : [permission];
  const canEdit =
    hydrated &&
    Boolean(href) &&
    permissions.some((p) => sessionHasPermission(session, p));

  if (!canEdit || !href) return null;

  const base =
    'group inline-flex items-center gap-1.5 font-poppins font-semibold backdrop-blur-md transition-[border-color,background-color,color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50';

  if (variant === 'hero') {
    return (
      <Link
        href={href}
        className={cn(
          base,
          'shrink-0 rounded-lg border border-white/25 bg-white/[0.1] px-2.5 py-1.5 text-[11px] tracking-wide text-white/90 sm:gap-2 sm:px-3 sm:text-xs',
          'hover:border-white/35 hover:bg-white/[0.14] hover:text-white',
          className
        )}
        aria-label={label}
      >
        <Pencil
          className="h-3.5 w-3.5 shrink-0 text-white/75 transition-colors duration-200 group-hover:text-white"
          aria-hidden
        />
        <span className="whitespace-nowrap">{label}</span>
      </Link>
    );
  }

  if (variant === 'inline') {
    return (
      <Link
        href={href}
        className={cn(
          base,
          'shrink-0 rounded-lg border px-2.5 py-1.5 text-[11px] tracking-wide sm:gap-2 sm:px-3 sm:text-xs',
          tone === 'dark'
            ? 'border-white/25 bg-white/[0.1] text-white/90 hover:border-white/35 hover:bg-white/[0.14] hover:text-white'
            : 'border-sky-300/50 bg-sky-50/90 text-sky-800 hover:border-sky-400 hover:bg-sky-50 hover:text-sky-900',
          className
        )}
        aria-label={label}
      >
        <Pencil
          className={cn(
            'h-3.5 w-3.5 shrink-0 transition-colors duration-200',
            tone === 'dark'
              ? 'text-white/75 group-hover:text-white'
              : 'text-sky-600/80 group-hover:text-sky-700'
          )}
          aria-hidden
        />
        <span className="whitespace-nowrap">{label}</span>
      </Link>
    );
  }

  return (
    <Link
      href={href}
      className={cn(
        base,
        'gap-2 rounded-xl px-3.5 py-2 text-sm shadow-md',
        'fixed top-[5.25rem] right-4 z-[60] sm:right-6',
        tone === 'dark'
          ? 'border border-sky-400/35 bg-[#030023]/90 text-sky-100 hover:border-sky-400/55 hover:bg-sky-500/20 hover:shadow-sky-500/15'
          : 'border border-sky-300/60 bg-white/95 text-sky-800 shadow-sky-900/10 hover:border-sky-400 hover:bg-sky-50',
        className
      )}
      aria-label={label}
    >
      <Pencil className="h-4 w-4 shrink-0" aria-hidden />
      {label}
    </Link>
  );
}
